/**
 * 답변 읽기용 remark 플러그인.
 * - `> [!TIP]` 같은 GitHub 알림 문법을 콜아웃으로 표시한다.
 * - 본문 `[n]` 은 출처 목록에 n번이 있을 때만 출처 번호 칩이 된다.
 * - 주소가 그대로 보이는 링크와 `([도메인](주소))` 출처 링크는 도메인 칩이 된다.
 */

export const CALLOUT_LABELS = {
  summary: '한눈에 보기',
  tip: '팁',
  note: '참고',
  important: '중요',
  warning: '주의',
  caution: '주의',
};

// 복사·읽어 주기용: 인용 첫 줄의 `[!TIP]` 표시를 사람이 읽는 이름표로 바꾼다.
export const replaceCalloutMarkers = (markdown) => String(markdown || '').replace(
  /^([ \t]*>[ \t]*)\[!(TIP|NOTE|IMPORTANT|WARNING|CAUTION|SUMMARY)\]([ \t]*)(\S?)/gim,
  (match, prefix, type, space, next) => `${prefix}${CALLOUT_LABELS[type.toLowerCase()]}${next ? `: ${next}` : ''}`,
);

const CALLOUT_RE = /^\[!(TIP|NOTE|IMPORTANT|WARNING|CAUTION|SUMMARY)\][ \t]*([^\n]*)(?:\n|$)/i;
const CALLOUT_TITLE_MAX = 40;
const CITE_RE = /\[(\d{1,2})\]/g;
// `arr[1]`, `x[2]` 처럼 영문·숫자·밑줄 바로 뒤의 [n] 은 코드·수식 첨자다(한글 바로 뒤 [n] 은 출처).
const INDEX_PREFIX_RE = /[A-Za-z0-9_]/;
const DOMAIN_TEXT_RE = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

const SKIP_TYPES = new Set(['link', 'linkReference', 'inlineCode', 'code', 'math', 'inlineMath', 'html', 'definition']);

const textOf = (node) => {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  return (node.children || []).map(textOf).join('');
};

export const getLinkHost = (url) => {
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    return parsed.hostname.replace(/^www\./, '');
  } catch (error) {
    return '';
  }
};

const addClass = (node, className, extra = {}) => {
  node.data = node.data || {};
  const props = node.data.hProperties || {};
  const classes = [].concat(props.className || [], className);
  node.data.hProperties = { ...props, ...extra, className: classes };
};

const markCallout = (node) => {
  const first = node.children?.[0];
  const firstText = first?.type === 'paragraph' ? first.children?.[0] : null;
  if (!firstText || firstText.type !== 'text') return;
  const match = firstText.value.match(CALLOUT_RE);
  if (!match) return;
  const type = match[1].toLowerCase();
  const sameLine = match[2].trim();
  const hasNewline = match[0].endsWith('\n');
  const markerEnd = match[0].length - match[2].length - (hasNewline ? 1 : 0);
  // `[!TIP] 실험 안전` 처럼 표시 뒤 같은 줄의 짧은 글은 상자 제목으로 쓴다.
  // 같은 줄에 굵게 같은 다른 노드가 이어지거나 글이 길면 본문 첫 줄로 둔다.
  const wholeLineInText = hasNewline || first.children.length === 1;
  const hasBody = firstText.value.length > match[0].length || first.children.length > 1 || node.children.length > 1;
  const title = sameLine && wholeLineInText && hasBody && sameLine.length <= CALLOUT_TITLE_MAX ? sameLine : '';
  firstText.value = firstText.value.slice(sameLine && !title ? markerEnd : match[0].length);
  if (!firstText.value) first.children.shift();
  if (first.children.length === 0) node.children.shift();
  addClass(node, ['wq-callout', `wq-callout--${type}`], {
    'data-callout': type,
    ...(title ? { 'data-callout-title': title } : {}),
  });
};

const isSourceLink = (node) => {
  const host = getLinkHost(node.url);
  if (!host) return false;
  const label = textOf(node).trim();
  if (!label) return false;
  const bareUrl = node.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  return label === node.url
    || label.replace(/\/$/, '') === bareUrl
    || (DOMAIN_TEXT_RE.test(label) && getLinkHost(`https://${label}`) === host);
};

const markSourceLinks = (parent) => {
  const children = parent.children || [];
  children.forEach((node, index) => {
    if (node.type !== 'link' || !isSourceLink(node)) return;
    const host = getLinkHost(node.url);
    node.children = [{ type: 'text', value: host }];
    addClass(node, 'wq-source-chip');
    const prev = children[index - 1];
    const next = children[index + 1];
    if (prev?.type === 'text' && next?.type === 'text' && /\($/.test(prev.value) && /^\)/.test(next.value)) {
      prev.value = prev.value.replace(/\s*\($/, ' ');
      next.value = next.value.slice(1);
    }
  });
};

const splitCitations = (parent, citations) => {
  const next = [];
  let changed = false;
  (parent.children || []).forEach((node) => {
    if (node.type !== 'text' || !node.value.includes('[')) {
      next.push(node);
      return;
    }
    let last = 0;
    let lastCiteEnd = -1;
    const value = node.value;
    value.replace(CITE_RE, (match, digits, offset) => {
      const before = value[offset - 1] || '';
      if (INDEX_PREFIX_RE.test(before) || (before === ']' && offset !== lastCiteEnd)) return match;
      const number = Number(digits);
      const source = citations[number - 1];
      if (!source?.url || !getLinkHost(source.url)) return match;
      let start = offset;
      let end = offset + match.length;
      if (value[start - 1] === '(' && value[end] === ')') {
        start -= 1;
        end += 1;
      }
      if (start > last) next.push({ type: 'text', value: value.slice(last, start) });
      const link = {
        type: 'link',
        url: source.url,
        title: source.title || getLinkHost(source.url),
        children: [{ type: 'text', value: String(number) }],
      };
      addClass(link, 'wq-cite', { 'data-cite': number });
      next.push(link);
      last = end;
      lastCiteEnd = offset + match.length;
      changed = true;
      return match;
    });
    if (last === 0) {
      next.push(node);
    } else if (last < value.length) {
      next.push({ type: 'text', value: value.slice(last) });
    }
  });
  if (changed) parent.children = next;
};

const walk = (node, citations) => {
  if (!node || SKIP_TYPES.has(node.type)) return;
  if (node.type === 'blockquote') markCallout(node);
  if (Array.isArray(node.children)) {
    markSourceLinks(node);
    if (citations.length > 0) splitCitations(node, citations);
    node.children.forEach((child) => walk(child, citations));
  }
};

export default function remarkReading(options = {}) {
  const citations = Array.isArray(options.citations) ? options.citations : [];
  return (tree) => {
    walk(tree, citations);
  };
}
